/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useLoaderData,
  type LoaderFunctionArgs,
  type MetaFunction
} from 'react-router'
import { dehydrate, QueryClient } from '@tanstack/react-query'

import { Feature, Ancestors, EntryCheck } from './~queries/pageQuery'

import HeroStandard from '~/components/Hero/Core/Standard/HeroStandard'
import HeroBespoke from '~/components/Hero/Core/Bespoke/HeroBespoke'

import { ComponentRenderer } from '~/layouts/renderers/layout/layoutRenderer'

import { colourMap, isDevelopment } from '~/utils/misc'
import RandomCmsLinks from '~/~DevelopmentComps/DevelopmentOnly/RandomCmsLinks/RandomCmsLinks'
import NextSteps from '~/components/Content/NextSteps/NextSteps'
import FootNote from '~/components/Text/FootNote/FootNote'
import { getEntryById } from '~/lib/contensis/misc'

export async function loader({ request }: LoaderFunctionArgs) {
  const queryClient = new QueryClient()
  const url = new URL(request.url)

  const entry = await EntryCheck(url)

  if (!entry?.entryTitle) {
    throw new Response('Not Found', { status: 404 })
  }

  await queryClient.prefetchQuery({
    queryKey: ['standardData'],
    queryFn: () => Feature(url)
  })

  await queryClient.prefetchQuery({
    queryKey: ['breadcrumbData'],
    queryFn: () => Ancestors(url)
  })

  const featureData = await queryClient.ensureQueryData({
    queryKey: ['standardData'],
    queryFn: () => Feature(url)
  })

  // Prefetch next steps if present
  if (featureData?.contentNextSteps?.sys?.id) {
    await queryClient.prefetchQuery({
      queryKey: ['next-steps', featureData.contentNextSteps.sys.id],
      queryFn: () => getEntryById(featureData.contentNextSteps.sys.id)
    })
  }

  return { dehydratedState: dehydrate(queryClient) }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const webUrl = isDevelopment
    ? 'https://live-websitemodern-uni-hull.cloud.contensis.com'
    : ''

  const meta = data?.dehydratedState?.queries[0]?.state?.data as unknown as Meta

  const backgroundColourName = meta?.heroContent?.backgroundColour
  const backgroundColourHex = backgroundColourName
    ? colourMap[backgroundColourName] || backgroundColourName // fallback to original if not mapped
    : '#0e1647'

  return [
    { title: `${meta?.seoStandard?.title} | University of Hull` },
    {
      name: 'description',
      content: `${meta?.seoStandard?.description}`
    },
    { rel: 'canonical', href: `${meta?.sys?.uri}` },
    { name: 'IDL:EntryId', content: `${meta?.sys?.id}` },
    { name: 'IDL:ProjectId', content: 'websiteModern' },
    { name: 'theme-color', content: backgroundColourHex },
    { name: 'msapplication-navbutton-color', content: backgroundColourHex },
    {
      name: 'apple-mobile-web-app-status-bar-style',
      content: backgroundColourHex
    },
    meta?.heroStandard[0]
      ? {
          rel: 'preload',
          as: 'image',
          href: `${meta?.heroStandard[0]?.image?.asset?.sys?.uri}`
        }
      : {
          rel: 'preload',
          as: 'image',
          href: `${meta?.heroBespoke[0]?.image?.asset?.sys?.uri}`
        },
    {
      property: 'og:description',
      content: `${meta?.seoStandard?.description}`
    },
    {
      property: 'og:image',
      content: `${webUrl}${meta?.seoStandard?.socialImage?.asset?.sys?.uri}`
    },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@UniOfHull' },
    { name: 'twitter:title', content: `${meta?.seoStandard?.title}` },
    {
      name: 'twitter:description',
      content: `${meta?.seoStandard?.description}`
    },
    {
      name: 'twitter:image',
      content: `${webUrl}${meta?.seoStandard?.socialImage?.asset?.sys?.uri}`
    },
    meta?.seoStandard?.noIndex && {
      name: 'robots',
      content: 'noindex'
    }
  ]
}

export default function StandardRoute() {
  const loaderData = useLoaderData<typeof loader>()
  const queries = loaderData?.dehydratedState?.queries

  const data = queries[0]?.state?.data as EntryDetails | undefined
  const breadcrumb = queries[1]?.state?.data

  const nextStepsId = data?.contentNextSteps?.sys?.id
  const nextStepsData = queries.find(
    q => q.queryKey[0] === 'next-steps' && q.queryKey[1] === nextStepsId
  )?.state?.data

  // const mainBody = loaderData.hydratedMainBody ?? []

  const heroStandard = Array.isArray(data?.heroStandard)
    ? data?.heroStandard[0]
    : data?.heroStandard

  const heroBespoke = Array.isArray(data?.heroBespoke)
    ? data?.heroBespoke[0]
    : data?.heroBespoke

  return (
    <>
      {data &&
        data?.heroContent &&
        (data?.heroType === 'bespoke' ? (
          <HeroBespoke
            data={{
              ...data?.heroContent,
              ...heroBespoke
            }}
            breadcrumb={breadcrumb}
          />
        ) : (
          <HeroStandard
            data={{
              ...data?.heroContent,
              ...heroStandard
            }}
            breadcrumb={breadcrumb}
          />
        ))}

      {isDevelopment && <RandomCmsLinks />}

      {data && (data as EntryDetails)?.mainBody !== undefined && (
        <ComponentRenderer components={(data as EntryDetails)?.mainBody} />
      )}

      {data && data.contentNextSteps && (
        <NextSteps
          content={
            nextStepsData as React.ComponentProps<typeof NextSteps>['content']
          }
        />
      )}

      {data?.footnotes &&
        (data?.footnotes.footnote.length > 0 ||
          data?.footnotes.copy !== null) && (
          <FootNote content={data?.footnotes} backgroundColour={'light-grey'} />
        )}
    </>
  )
}
